import * as S from "sanctuary";

import { openPayment } from "@/components/payment/Payment.state";
import {
  getTransactionSearchRequestPending,
  getTransactionDateFilter,
  getTransactionsSearchRequestError,
} from "@/components/transactionSearchPage/TransactionSearchPage.state";
import { getSearchPendingText } from "@/components/transactionSearchPage/TransactionSearchPage.state";
import { getUserTimezone } from "@/components/currentUser/CurrentUser.state";
export const RESULTS_PER_PAGE = 20;

export const PAYMENT_INITIATOR_SCHEDULE_SERVICE = "schedule_service";

// `searchId` names the search these rows came back from, so a response that
// lost the race cannot land on a newer search's rows (WCHK-3037).
// `clientPaginated` marks these rows as the whole set, paged here.
export const SET_TRANSACTION_SEARCH_RESULTS =
  "transactionSearchResults/SET_TRANSACTION_SEARCH_RESULTS";
export const setTransactionSearchResults = ({
  transactions,
  resultsCount,
  page,
  searchId = null,
  clientPaginated = false,
}) => ({
  type: SET_TRANSACTION_SEARCH_RESULTS,
  payload: {
    transactions,
    resultsCount,
    page,
    searchId,
    clientPaginated,
  },
});

// A starting search empties the slice and records itself as the search it is
// waiting on. A caller that only wants it emptied names no search.
export const CLEAR_STATE = "transactionSearchResults/CLEAR_STATE";
export const clearState = ({ searchId = null } = {}) => ({
  type: CLEAR_STATE,
  payload: { searchId },
});

export const SET_CURRENT_PAGE = "transactionSearchResults/SET_CURRENT_PAGE";
export const setCurrentPage = ({ pageNumber }) => ({
  type: SET_CURRENT_PAGE,
  payload: { pageNumber },
});

export const PAGE_NEXT = "transactionSearchResults/PAGE_NEXT";
export const pageNext = () => ({
  type: PAGE_NEXT,
});

export const PAGE_PREVIOUS = "transactionSearchResults/PAGE_PREVIOUS";
export const pagePrevious = () => ({
  type: PAGE_PREVIOUS,
});

const INITIAL_STATE = {
  transactions: S.Nothing,
  totalSearchResultsCount: 0,
  searchResultsPageCount: 1,
  currentPage: 1,
  // The search this slice is waiting on, or null when nothing named one.
  activeSearchId: null,
  clientPaginated: false,
};

/**
 * isStaleSearchResult
 *
 * Whether a settling response belongs to a search the slice has already moved
 * past. Both sides have to name a search for that to be knowable, so an
 * untagged response is applied exactly as before.
 * @param {object} state - The results slice.
 * @param {object} payload - The SET_TRANSACTION_SEARCH_RESULTS payload.
 * @returns {boolean} - True when the response belongs to a superseded search.
 */
const isStaleSearchResult = (state, payload) =>
  !!state.activeSearchId &&
  !!payload?.searchId &&
  payload.searchId !== state.activeSearchId;

const reducer = (state = INITIAL_STATE, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_STATE:
      return { ...INITIAL_STATE, activeSearchId: payload?.searchId ?? null };
    case SET_TRANSACTION_SEARCH_RESULTS:
      // The lifecycle already ignores a superseded search's settlement; its
      // rows have to be dropped here too (WCHK-3037).
      if (isStaleSearchResult(state, payload)) return state;
      return {
        ...state,
        transactions: S.Just(payload.transactions),
        totalSearchResultsCount: payload.resultsCount,
        searchResultsPageCount: Math.max(
          1,
          Math.ceil(payload.resultsCount / RESULTS_PER_PAGE)
        ),
        currentPage: payload.page,
        clientPaginated: !!payload.clientPaginated,
      };
    case PAGE_NEXT:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(state.currentPage + 1),
      };
    case PAGE_PREVIOUS:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(state.currentPage - 1),
      };
    case SET_CURRENT_PAGE:
      return {
        ...state,
        currentPage: S.clamp(1)(state.searchResultsPageCount)(payload.pageNumber),
      };
    default:
      return state;
  }
};

const getState = (state) => state.transactionSearchResults;
export const getCurrentPage = (state) => getState(state).currentPage;
export const getCurrentPageZeroIndexed = (state) => getCurrentPage(state) - 1;
export const getIsClientPaginated = (state) => getState(state).clientPaginated;
export const getActiveSearchId = (state) => getState(state).activeSearchId;

// The rows for the current page; a client-paged set is sliced here.
export const getVisibleTransactions = (state) => {
  const { transactions, clientPaginated, currentPage } = getState(state);
  if (!clientPaginated || S.isNothing(transactions)) return transactions;
  const start = (currentPage - 1) * RESULTS_PER_PAGE;
  return S.Just(transactions.value.slice(start, start + RESULTS_PER_PAGE));
};

export const mapDispatchToProps = {
  setCurrentPage,
  pageNext,
  pagePrevious,
  openTransactionDetails: (transactionId) => openPayment({ transactionId }),
};

export const mapStateToProps = (state) => ({
  ...getState(state),
  transactions: getVisibleTransactions(state),
  dateFilter: getTransactionDateFilter(state),
  searchRequestPending: getTransactionSearchRequestPending(state),
  searchRequestError: getTransactionsSearchRequestError(state),
  userTimezone: getUserTimezone(state),
  searchPendingText: getSearchPendingText(state),
});

export default reducer;
